import React from "react";
import PropTypes from "prop-types";
import { partial, not, equals, propEq, find } from "ramda";
import { withLDConsumer } from "launchdarkly-react-client-sdk";

import { selectedOptionOfAvailable } from "helpers/online_ordering/cart";

import Icon from "components/icon";
import Modal from "components/modal";
import Loader from "components/loader";
import { OrderConfirmModalTestIds } from "./order_confirm_modal.testids";

function formatStoreAddress(address) {
  const { line1, line2, city, state } = address;

  if (equals(line2, "")) {
    return `${line1}, ${city} ${state}`;
  }

  return `${line1}, ${line2}, ${city} ${state}`;
}

function OrderConfirmModal({
  onUpdateOrderStatus,
  orderingOptions,
  storeInfo,
  checkout,
  consumer,
}) {
  const {
    status: { orderStatus, confirmationCode, orderTime, isOrderAhead },
  } = checkout;

  const { phoneNumber, address } = storeInfo;
  const {
    contactInfo: { firstName, lastName, email },
  } = consumer;

  const selectedOptionName = selectedOptionOfAvailable(
    orderingOptions.selectedTransport,
    storeInfo.fulfillmentTypes
  );
  const transportMethod = find(
    propEq("name", selectedOptionName),
    storeInfo.fulfillmentTypes
  );

  if (equals(orderStatus, "submitting")) {
    return (
      <Modal className="order-confirm-modal">
        <div className="modal-content">
          <div className="center">
            <div>
              <h2>We're submitting your order...</h2>
              <Loader />
            </div>
          </div>
        </div>
      </Modal>
    );
  }

  let transportSpecificDetails;
  if (
    equals(orderingOptions.selectedTransport, "Delivery") ||
    equals(orderingOptions.selectedTransport, "Pick up")
  ) {
    let orderReadyTime = `${orderTime
      .clone()
      .add(transportMethod.estimate[0], "minutes")
      .format("h:mm a")} - ${orderTime
      .clone()
      .add(transportMethod.estimate[1], "minutes")
      .format("h:mm a")}`;

    if (isOrderAhead) {
      orderReadyTime = orderTime.format("dddd, MMMM Do YYYY, h:mm A z");
    }

    if (equals(orderingOptions.selectedTransport, "Delivery")) {
      let customerAddressLine2;
      if (not(equals(orderingOptions.deliveryAddressLine2, ""))) {
        customerAddressLine2 = (
          <div className="customer-address">
            {orderingOptions.deliveryAddressLine2}
          </div>
        );
      }

      transportSpecificDetails = (
        <div
          className="delivery-confirmation"
          data-testid={OrderConfirmModalTestIds.deliveryConfirmation}>
          <h2 className="time-estimate">
            Estimated delivery time: {orderReadyTime}
          </h2>
          <div className="delivery-address">
            <div>Delivering to:</div>
            <div className="customer-name">{`${firstName} ${lastName}`}</div>
            <div className="customer-address">
              {orderingOptions.deliveryAddress}
            </div>
            {customerAddressLine2}
          </div>
        </div>
      );
    } else if (equals(orderingOptions.selectedTransport, "Pick up")) {
      transportSpecificDetails = (
        // CHANGE THIS TO BE OFF OF ORDER PLACED TIME NOT CURRENT TIME
        <div
          className="pickup-confirmation"
          data-testid={OrderConfirmModalTestIds.deliveryConfirmation}>
          <h2 className="time-estimate">
            Estimated pick up time: {orderReadyTime}
          </h2>
        </div>
      );
    }
  }

  return (
    <Modal
      className="order-confirm-modal"
      onClose={partial(onUpdateOrderStatus, ["pending"])}>
      <div
        className="modal-content"
        data-testid={OrderConfirmModalTestIds.modal}>
        <h2>Thank you for your order!</h2>
        <Icon className="check-icon" type="circle-check" />

        {transportSpecificDetails}

        <div className="store-details">
          <h5 className="store-name">{storeInfo.locationName}</h5>
          <div className="store-address">{formatStoreAddress(address)}</div>
          <div className="store-phone">{phoneNumber}</div>
        </div>
        <div className="email-message">
          We'll send an email confirmation to{" "}
          <span className="email">{email}</span> to let you know your order is
          in.
        </div>
        <div className="order-number">
          Your order number is {confirmationCode}
        </div>
      </div>
    </Modal>
  );
}

OrderConfirmModal.propTypes = {
  orderingOptions: PropTypes.object.isRequired,
  consumer: PropTypes.object.isRequired,
  storeInfo: PropTypes.object.isRequired,
  checkout: PropTypes.object.isRequired,
  onUpdateOrderStatus: PropTypes.func.isRequired,
  flags: PropTypes.object.isRequired,
};

export default withLDConsumer()(OrderConfirmModal);
